import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  MultiFactorSession,
  MultiFactorUser,
} from 'firebase/auth';
import { auth } from '../firebase';

interface SecurityContextType {
  verifyCaptcha: () => Promise<string | null>;
  isVerified: boolean;

  // MFA — backed by Firebase TOTP MFA.
  beginMFAEnrollment: () => Promise<{ secret: TotpSecret; qrCodeUrl: string }>;
  finalizeMFAEnrollment: (secret: TotpSecret, code: string, displayName?: string) => Promise<void>;
  isMFAEnabled: () => boolean;
  unenrollMFA: () => Promise<void>;
}

const SecurityContext = createContext<SecurityContextType | null>(null);

export const useSecurity = () => {
  const ctx = useContext(SecurityContext);
  if (!ctx) throw new Error('useSecurity must be used within a SecurityProvider');
  return ctx;
};

interface SecurityProviderProps {
  children: ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) return; // No site key configured; skip.

    if (!(window as any).grecaptcha && !document.querySelector('script[src*="recaptcha"]')) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => setRecaptchaLoaded(true);
      document.head.appendChild(script);
    } else {
      setRecaptchaLoaded(true);
    }
  }, []);

  const verifyCaptcha = async (): Promise<string | null> => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey || !recaptchaLoaded || !(window as any).grecaptcha) {
      console.warn('reCAPTCHA not loaded or not configured');
      return null;
    }
    try {
      const token = await (window as any).grecaptcha.execute(siteKey, { action: 'submit' });
      setIsVerified(true);
      return token;
    } catch (err) {
      console.error('reCAPTCHA verification failed:', err);
      setIsVerified(false);
      return null;
    }
  };

  // --- Real MFA backed by Firebase TOTP ---
  // The user must be signed in (and recently authenticated) to enroll a second factor.

  const requireMfaUser = (): MultiFactorUser => {
    if (!auth.currentUser) throw new Error('Sign-in required to manage MFA.');
    return multiFactor(auth.currentUser);
  };

  const beginMFAEnrollment = async (): Promise<{ secret: TotpSecret; qrCodeUrl: string }> => {
    const mfaUser = requireMfaUser();
    const session: MultiFactorSession = await mfaUser.getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    const issuer = 'Superior Payroll';
    const accountName = auth.currentUser?.email || auth.currentUser?.uid || 'user';
    const qrCodeUrl = secret.generateQrCodeUrl(accountName, issuer);
    return { secret, qrCodeUrl };
  };

  const finalizeMFAEnrollment = async (
    secret: TotpSecret,
    code: string,
    displayName = 'Authenticator app'
  ): Promise<void> => {
    const mfaUser = requireMfaUser();
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
    await mfaUser.enroll(assertion, displayName);
  };

  const isMFAEnabled = (): boolean => {
    if (!auth.currentUser) return false;
    return multiFactor(auth.currentUser).enrolledFactors.length > 0;
  };

  const unenrollMFA = async (): Promise<void> => {
    const mfaUser = requireMfaUser();
    const factors = mfaUser.enrolledFactors;
    if (factors.length === 0) return;
    await mfaUser.unenroll(factors[0]);
  };

  const value: SecurityContextType = {
    verifyCaptcha,
    isVerified,
    beginMFAEnrollment,
    finalizeMFAEnrollment,
    isMFAEnabled,
    unenrollMFA,
  };

  return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
};

declare global {
  interface Window {
    grecaptcha: {
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      ready: (callback: () => void) => void;
    };
  }
}

export default SecurityContext;
